import { Button, YoutubeIcon } from 'components/atoms';
import { useHasPermission } from 'hooks';
import { useCountdown } from 'hooks/useCountdown';
import { useFirstAccessibleChildPath } from 'hooks/useFirstAccessibleChildPath';
import { useStoreState } from 'providers/StoreStateContext';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiCaretDownBold, PiGearBold, PiPowerBold } from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetUnreadNotification } from 'services/notifications/index';
import { useGetLicenceConfiguration } from 'services/settings/individual';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { INavbarItem } from 'types/navbar/navbar.interface';
import { getParentPathname } from 'utils/navbar/getParentPathname';
import { navbarItems } from 'utils/navbar/navbarItems';
import './Navbar.scss';

const Navbar = () => {
  const [showChildren, setShowChildren] = useState<Record<string, boolean>>({});
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { clearTokens, tokenData, applicationSettings } = useStoreState();

  const hasPermission = useHasPermission();

  const hasNotificationPermission = useMemo(
    () => hasPermission('NotificationPermissionsLIST**', 32),
    [hasPermission]
  );

  const productsFirstPath = useFirstAccessibleChildPath('music-product-digital');
  const contractsFirstPath = useFirstAccessibleChildPath('contracts');
  const settlementsFirstPath = useFirstAccessibleChildPath('statements');
  const ordersFirstPath = useFirstAccessibleChildPath('settlement-order');
  const clientFirstPath = useFirstAccessibleChildPath('client-users');

  const firstAccessiblePaths = useMemo(() => ({
    'music-product-digital': productsFirstPath,
    'contracts': contractsFirstPath,
    'statements': settlementsFirstPath,
    'settlement-order': ordersFirstPath,
    'client-users': clientFirstPath,
  }), [productsFirstPath, contractsFirstPath, settlementsFirstPath, ordersFirstPath, clientFirstPath]);

  const { data, refetch } = useGetUnreadNotification(hasNotificationPermission);
  const { startCounter, isCountingDown } = useCountdown(60, false);
  const getLicenceConfiguration = useGetLicenceConfiguration();

  useEffect(() => {
    if (!isCountingDown && hasNotificationPermission) {
      refetch();
      startCounter();
    }
  }, [isCountingDown, undefined]);

  const customStyle = { justifyContent: 'start' };

  const isPageActive = (name: string): boolean =>
    getParentPathname(pathname) === name;

  const toggleChildren = (key: string) => {
    setShowChildren(prev => ({ [key]: !prev[key] }));
  };

  const navigateToPath = (item: INavbarItem, key: string) => {
    if (item.children) {
      toggleChildren(key);
      if (showChildren[key]) {
        return;
      }
      
      const firstAccessiblePath = firstAccessiblePaths[item.path as keyof typeof firstAccessiblePaths];
      if (firstAccessiblePath) {
        navigate(`/${firstAccessiblePath}`);
        return;
      }
    }
    
    navigate(`/${item.path}`);
  };

  return (
    <div className='navbar-wrapper'>
      {!!getLicenceConfiguration.data &&
        !!getLicenceConfiguration.data.isDemo && (
          <div className='navbar-demo-wrapper'>
            <p className='text'>Demo</p>
          </div>
        )}
      <div className='buttons'>
        {navbarItems(
          getLicenceConfiguration.data?.clientModuleAccess,
          applicationSettings?.extensionContract
        ).map((item, key) =>
          item.permissionNames &&
          tokenData &&
          !item.permissionNames?.find(
            name => tokenData[name as keyof typeof tokenData]
          ) ? null : (
            <div
              key={key}
              className={`button-wrapper ${
                showChildren[key] ? 'show-sub-menu' : ''
              }`}>
              {item.path === 'notifications' ? (
                <div className='notification-counter'>
                  <Button
                    isClear
                    buttonStyle={customStyle}
                    fullWidth
                    isActive={isPageActive(item.path)}
                    onClick={() => navigateToPath(item, key.toString())}
                    className='button'>
                    <Button.Icon icon={<item.icon />} />
                    <Button.Text text={t(`navbar.${item.name ?? item.path}`)} />
                  </Button>
                  {!data ? null : (
                    <span
                      className={`notification-count ${
                        data > 99 ? 'two-digits' : ''
                      }`}>
                      {data > 99 ? '99+' : data}
                    </span>
                  )}
                </div>
              ) : (
                <Button
                  isClear
                  buttonStyle={customStyle}
                  fullWidth
                  isActive={
                    item.children?.length
                      ? !!item.children.find(({ path }) => isPageActive(path))
                      : isPageActive(item.path)
                  }
                  onClick={() => navigateToPath(item, key.toString())}
                  className='button'>
                  <Button.Icon icon={<item.icon />} />
                  <Button.Text text={t(`navbar.${item.name ?? item.path}`)} />

                  {item.children && (
                    <Button.Icon
                      icon={<PiCaretDownBold className='menu-arrow' />}
                    />
                  )}
                </Button>
              )}
              {item.children && (
                <div className={`sub-menu`}>
                  {item.children.map((child, childKey) =>
                    child.permissionNames &&
                    tokenData &&
                    !child.permissionNames?.find(
                      name => tokenData[name as keyof typeof tokenData]
                    ) ? null : (
                      <Button
                        key={childKey}
                        isClear
                        size='compact'
                        buttonStyle={customStyle}
                        fullWidth
                        variant={
                          isPageActive(child.path) && child.path === 'channel'
                            ? BUTTON_VARIANT.remove
                            : undefined
                        }
                        isActive={
                          child.path === 'channel'
                            ? false
                            : isPageActive(child.path)
                        }
                        onClick={() => navigate(`/${child.path}`)}
                        className='button'>
                        <Button.Icon
                          icon={
                            child.path === 'channel' ? (
                              <YoutubeIcon />
                            ) : (
                              <child.icon />
                            )
                          }
                        />
                        <Button.Text
                          text={t(`navbar.${child.name ?? child.path}`)}
                        />
                      </Button>
                    )
                  )}
                </div>
              )}
            </div>
          )
        )}
      </div>
      <div className='footer'>
        {/* <img
          src={`${process.env.PUBLIC_URL}/icons/magellup_logo.png`}
          alt='magellan_logo'
        /> */}
        <div className='footer-buttons'>
          <Button
            variant={BUTTON_VARIANT.default}
            fullWidth
            isClear
            onClick={() => navigate('/settings')}>
            <Button.Icon icon={<PiGearBold />} />
            <Button.Text text={t('navbar.settings')} />
          </Button>
          <Button
            variant={BUTTON_VARIANT.default}
            fullWidth
            onClick={clearTokens}>
            <Button.Icon icon={<PiPowerBold />} />
            <Button.Text text={t('navbar.logout')} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Navbar;
